import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Loader2, Plus, Trash2, RotateCcw, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

const CRIMSON = "var(--brand)";
const THINKING = [
  { v: "off", l: "Off", d: "No extra reasoning. Fastest and cheapest." },
  { v: "low", l: "Low", d: "A little reasoning before answering." },
  { v: "medium", l: "Medium", d: "Balanced — recommended for building and editing forms." },
  { v: "high", l: "High", d: "Most thorough. Slowest and costliest; for very complex forms." },
];
const STYLES = [
  { v: "adaptive", l: "Adaptive (Claude 4.6+)" },
  { v: "budget", l: "Token budget (older Claude)" },
  { v: "none", l: "Not supported" },
];

const Section = ({ title, hint, children }) => (
  <div className="rounded-lg border border-slate-200 p-3.5">
    <div className="text-sm font-semibold text-slate-800">{title}</div>
    {hint && <p className="text-[11px] text-slate-500 mt-0.5 mb-2.5">{hint}</p>}
    {children}
  </div>
);

// Admin screen for the AI Document Builder: model, thinking, output limits and the prompts.
export function AiSettingsDialog({ open, onOpenChange }) {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.docAiConfig().then((c) => {
    setCfg(c);
    const s = structuredClone(c.settings);
    s.promptText = Object.fromEntries(c.prompt_keys.map((k) => [k, s.prompts?.[k] ?? c.defaults.prompts[k]]));
    setDraft(s);
  }).catch(() => toast.error("Couldn't load the AI settings."));

  useEffect(() => { if (open) { setCfg(null); setDraft(null); load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (fn) => setDraft((d) => { const c = structuredClone(d); fn(c); return c; });
  const customised = (k) => (draft.promptText[k] || "").trim() !== (cfg.defaults.prompts[k] || "").trim();

  const resetAll = () => set((c) => {
    const d = structuredClone(cfg.defaults);
    Object.assign(c, { model: d.model, thinking: d.thinking, chat: d.chat, build: d.build, model_limits: d.model_limits, thinking_budgets: d.thinking_budgets });
    c.promptText = { ...d.prompts };
  });

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        model: draft.model, thinking: draft.thinking,
        chat: draft.chat, build: draft.build,
        thinking_budgets: draft.thinking_budgets, model_limits: draft.model_limits,
        prompts: draft.promptText,
      };
      await api.docAiSetConfig(body);
      toast.success("AI settings saved.");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the settings.");
    } finally { setSaving(false); }
  };

  const num = (path, k, props = {}) => (
    <Input type="number" value={draft[path][k]} onChange={(e) => set((c) => { c[path][k] = e.target.value; })} className="h-8 text-sm" {...props} />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0" data-testid="ai-settings-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="text-base font-brand flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" style={{ color: CRIMSON }} />AI Document Builder settings</DialogTitle>
          <p className="text-[11px] text-slate-500">Applies to all new chats and builds. Changing the model may mean changing the limits and prompts below.</p>
        </DialogHeader>

        {!draft ? (
          <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="overflow-y-auto px-5 py-4 space-y-3.5">
            <Section title="Model" hint="Choose from the models installed under the main AI Settings, or type any Bedrock model ID.">
              <div className="grid sm:grid-cols-2 gap-2.5">
                <div>
                  <Label className="text-[10px]">Installed models</Label>
                  <Select value={cfg.models.some((m) => m.id === draft.model) ? draft.model : ""} onValueChange={(v) => set((c) => { c.model = v; })}>
                    <SelectTrigger className="h-8 text-sm mt-1" data-testid="aiset-model"><SelectValue placeholder="Custom model ID" /></SelectTrigger>
                    <SelectContent>{cfg.models.map((m) => <SelectItem key={m.id} value={m.id} className="text-xs">{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px]">Model ID</Label>
                  <Input value={draft.model} onChange={(e) => set((c) => { c.model = e.target.value.trim(); })} className="h-8 text-sm mt-1 font-mono" data-testid="aiset-model-id" />
                </div>
              </div>
            </Section>

            <Section title="Thinking" hint="Lets the AI reason before it answers. Helps with complex forms; costs more and takes longer. Only used by models marked as supporting it in the limits table.">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {THINKING.map((t) => (
                  <button key={t.v} type="button" onClick={() => set((c) => { c.thinking = t.v; })} data-testid={`aiset-thinking-${t.v}`}
                    className={`rounded-md border p-2 text-left ${draft.thinking === t.v ? "border-brand bg-brand-soft" : "border-slate-200 hover:bg-slate-50"}`}>
                    <div className="text-xs font-semibold text-slate-800">{t.l}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{t.d}</div>
                  </button>
                ))}
              </div>
            </Section>

            <Section title="Output limits and temperature" hint="Max output tokens caps how long a reply or a built form can be (capped again by the model limits below). Temperature (0–1) is ignored while thinking is on.">
              <div className="grid grid-cols-[1fr_1fr_1fr] gap-2.5 items-end text-[10px]">
                <div />
                <Label>Max output tokens</Label><Label>Temperature</Label>
                <div className="text-xs font-medium text-slate-700 pb-2">Chat replies</div>
                {num("chat", "max_tokens", { "data-testid": "aiset-chat-max" })}{num("chat", "temperature", { step: "0.1", min: 0, max: 1 })}
                <div className="text-xs font-medium text-slate-700 pb-2">Building the form</div>
                {num("build", "max_tokens", { "data-testid": "aiset-build-max" })}{num("build", "temperature", { step: "0.1", min: 0, max: 1 })}
              </div>
            </Section>

            <Section title="Model limits" hint="Per-model capabilities. The first row whose text appears in the model ID applies; leave one row with empty text as the catch-all.">
              <div className="space-y-1.5">
                <div className="grid grid-cols-[1.4fr_1fr_1.3fr_auto] gap-2 text-[10px] text-slate-500"><span>Model ID contains</span><span>Max output</span><span>Thinking</span><span /></div>
                {draft.model_limits.map((r, i) => (
                  <div key={i} className="grid grid-cols-[1.4fr_1fr_1.3fr_auto] gap-2 items-center">
                    <Input value={r.match} placeholder="(any model)" onChange={(e) => set((c) => { c.model_limits[i].match = e.target.value; })} className="h-8 text-xs font-mono" />
                    <Input type="number" value={r.max_output} onChange={(e) => set((c) => { c.model_limits[i].max_output = e.target.value; })} className="h-8 text-xs" />
                    <Select value={r.thinking} onValueChange={(v) => set((c) => { c.model_limits[i].thinking = v; })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{STYLES.map((s) => <SelectItem key={s.v} value={s.v} className="text-xs">{s.l}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => set((c) => { c.model_limits.splice(i, 1); })} disabled={draft.model_limits.length <= 1}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => set((c) => { c.model_limits.push({ match: "", max_output: 8192, thinking: "none" }); })}><Plus className="h-3.5 w-3.5 mr-1" />Add row</Button>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100">
                <div className="text-[11px] font-medium text-slate-700">Thinking budgets (token-budget models only)</div>
                <div className="grid grid-cols-3 gap-2.5 mt-1.5">
                  {["low", "medium", "high"].map((l) => (
                    <div key={l}><Label className="text-[10px] capitalize">{l}</Label>
                      <Input type="number" value={draft.thinking_budgets[l]} onChange={(e) => set((c) => { c.thinking_budgets[l] = e.target.value; })} className="h-8 text-xs mt-1" /></div>
                  ))}
                </div>
              </div>
            </Section>

            <Section title="Prompts" hint="The instructions the AI follows. Edit with care — a poor prompt makes forms worse. Reset any prompt to the built-in default at any time. {{context}} and {{schema}} insert the shared parts; the form's own JSON, and the rule that lets the AI mark a reply as a proposed plan (which unlocks Preview), are added automatically.">
              <Accordion type="multiple" className="w-full">
                {cfg.prompt_keys.map((k) => (
                  <AccordionItem key={k} value={k}>
                    <AccordionTrigger className="text-xs py-2.5 hover:no-underline">
                      <span className="flex items-center gap-2 text-left">{cfg.prompt_titles[k]}{customised(k) && <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-700">customised</Badge>}</span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <Textarea value={draft.promptText[k]} onChange={(e) => set((c) => { c.promptText[k] = e.target.value; })} rows={14}
                        className="font-mono text-[11px] leading-relaxed" data-testid={`aiset-prompt-${k}`} />
                      <div className="mt-1.5 flex justify-end">
                        <Button variant="outline" size="sm" className="h-7 text-xs" disabled={!customised(k)}
                          onClick={() => set((c) => { c.promptText[k] = cfg.defaults.prompts[k]; })}><RotateCcw className="h-3 w-3 mr-1" />Reset to default</Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Section>
          </div>
        )}

        <DialogFooter className="px-5 py-3 border-t border-slate-100 sm:justify-between">
          <Button variant="ghost" size="sm" onClick={resetAll} disabled={!draft} className="text-xs text-slate-500"><RotateCcw className="h-3 w-3 mr-1" />Reset everything to defaults</Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={!draft || saving} style={{ backgroundColor: CRIMSON }} className="text-white" data-testid="aiset-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}Save settings
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
